import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, PencilIcon } from "@heroicons/react/24/solid";
import { usePermissions } from "../../contexts/PermissionContext";
import { useFeatureFlag } from "../../contexts/FeatureFlagContext";
import { FeatureFlag } from "../../constants/featureFlags";
import {
  BottomNavigationBarType,
  ComponentEnum,
  LessonI,
  ResponseEnum,
  AnalyticsCategoryEnum,
  BookmarkTypeEnum,
} from "../../types";
import { analytics } from "../../services/AnalyticsService";
import { PriorityListRef } from "../CreateLessonPage/components/contentComponents/PriorityListComponent";
import {
  useCreateResponse,
  useDeleteRecap,
  useUpdateRecap,
  useCreateLessonFeedback,
  useCreateOrGetRecap,
  useCreatePageBookmark,
} from "../../api/mutations";
import {
  useGetLesson,
  useGetTopic,
  useGetRecaps,
  useGetUserPreferences,
  useGetPageBookmarks,
} from "../../api/queries";
import Progress, { ProgressRef } from "../../components/Progress";
import Spinner from "../../componentLibrary/Spinner";
import { useNotification } from "../../hooks/useNotification";
import Button from "../../componentLibrary/Button";
import { renderPageComponents } from "../CreateLessonPage/utils/renderComponent";
import { parsePreviousAnswers } from "../CreateLessonPage/utils/parsePreviousAnswers";
import { Feedback } from "../../componentLibrary/Feedback";
import { FeedbackI } from "../../types";
import { ForEachComponentRef } from "../CreateLessonPage/components/contentComponents/ForEachComponent";
import { hasAnswersToSave } from "../../utils/lessonCompletion";
import LessonCompletionButton from "../../components/LessonCompletionButton";
import LoadingCard from "../../components/LoadingCard";
import { ErrorDisplay } from "../../utils/ErrorHandling";
import LessonTopNavigation from "../../components/LessonTopNavigation";
import LessonBottomNavigation from "../../components/LessonBottomNavigation";
import { FadingNavigation } from "../../componentLibrary/FadingBar";
import { useHasToolboxContent } from "../../hooks/useHasToolboxContent";
import GoToToolboxButton from "../../components/GoToToolboxButton";
import { buildPageBookmarkPayload } from "../../utils/buildPageBookmarkPayload";
import KeyTakeaways from "./components/KeyTakeaways";
import LessonSideNav from "./components/LessonSideNav";

type RecapValue = {
  label: string;
  value: string | string[];
  displayAs?: string;
};

type RecapEntry = {
  [key: string]: RecapValue | undefined;
};

type RecapContent = RecapEntry[];

const LessonPage = () => {
  const location = useLocation();
  const { notify, clearNotifications } = useNotification();
  const { hasPermission } = usePermissions();
  const canEditContent = hasPermission("content:update");

  let initialPage = location.state?.initialPage ?? 0;

  const searchParams = new URLSearchParams(location.search);
  const pageFromQuery = searchParams.get("page");
  initialPage = pageFromQuery ? parseInt(pageFromQuery, 10) : initialPage;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [recapState, setRecapState] = useState<any[]>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [visitedPageIds, setVisitedPageIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<FeedbackI | null>(null);
  const hasTrackedLessonView = useRef<boolean>(false);

  const progressRef = useRef<ProgressRef>(null);
  const keyTakeawaysProgressRef = useRef<ProgressRef>(null);

  // Storing PriorityList component refs to call function present inside the component
  const priorityListRefs = useRef<Record<string, PriorityListRef | null>>({});

  // Create refs for components
  const forEachRefs = useRef<Record<string, ForEachComponentRef | null>>({});

  const synchForEach = (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answers: Record<string, any>,
  ) => {
    Object.entries(forEachRefs.current).forEach(([, ref]) => {
      if (ref && typeof ref.syncVariableChange === "function") {
        ref.syncVariableChange(variable, answers);
      }
    });
  };

  const { lessonId, moduleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // URL params may be slugs or ObjectIds — API accepts both.
  // For mutations (recaps, responses), use actual ObjectIds from fetched data.

  const responseMut = useCreateResponse({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["responses"] });
    },
  });

  const {
    data: lesson,
    isError,
    isLoading,
    error: lessonError,
  } = useGetLesson(lessonId);
  const { data: module } = useGetTopic(moduleId);
  const {
    data: recaps,
    isError: isErrorRecap,
    isLoading: isLoadingRecap,
    error: recapsError,
  } = useGetRecaps(lessonId);
  const { data: userPreferences } = useGetUserPreferences();
  const { hasContent: hasToolboxContent } = useHasToolboxContent(moduleId);
  const topicTocEnabled = useFeatureFlag(FeatureFlag.TOPIC_TOC);

  // Resolve actual ObjectIds from fetched data for mutations
  const moduleObjectId = module?._id;
  const lessonObjectId = lesson?._id;

  // Slug-based URL for the current page (used for toolbox "from" links)
  const moduleSlugOrId = module?.slug ?? moduleId;
  const lessonSlugOrId = lesson?.slug ?? lessonId;
  const learnUrl = `/topics/${moduleSlugOrId}/sections/${lessonSlugOrId}?page=${currentPage}`;

  const currentPageId = lesson?.pages?.[currentPage]?._id;
  const shouldFetchPageBookmarks = Boolean(currentPageId && moduleObjectId);
  const { data: currentPageBookmarks } = useGetPageBookmarks(
    currentPageId ?? undefined,
    moduleObjectId ?? undefined,
    { enabled: shouldFetchPageBookmarks },
  );
  const isPageInToolbox = Boolean(
    currentPageId && (currentPageBookmarks?.length ?? 0) > 0,
  );

  const mutation = useUpdateRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
  });

  const deleteMut = useDeleteRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
  });

  const createPageBookmarkMut = useCreatePageBookmark({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          "bookmarks",
          BookmarkTypeEnum.PAGE,
          currentPageId ?? undefined,
          moduleObjectId ?? undefined,
        ],
      });
    },
  });

  const createOrGetRecapMut = useCreateOrGetRecap({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recaps"] });
    },
    onError: async () => {
      notify("error", "There was an error creating a new recap.");
    },
  });

  const feedbackMut = useCreateLessonFeedback({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["feedback"] });
    },
    onError: () => {
      notify(
        "error",
        "There was an error saving your feedback. Please try again.",
      );
    },
  });

  // Reset currentPage when lessonId changes
  useEffect(() => {
    setCurrentPage(initialPage);
    hasTrackedLessonView.current = false; // Reset tracking flag when lesson changes
  }, [lessonId]);

  // Track lesson view (only once when lesson loads)
  useEffect(() => {
    if (lesson && lessonObjectId && !hasTrackedLessonView.current) {
      analytics.page({
        category: AnalyticsCategoryEnum.Lesson,
        elementId: lessonObjectId,
        additionalDetails: {
          moduleId: moduleObjectId,
        },
      });
      hasTrackedLessonView.current = true;
    }
  }, [lesson, lessonObjectId, moduleObjectId]);

  useEffect(() => {
    if (!lesson || !module || !lessonObjectId || !moduleObjectId) return;
    const page = lesson.pages[currentPage];
    if (!page || !page._id) return;

    analytics.page({
      category: AnalyticsCategoryEnum.Page,
      elementId: page._id,
      additionalDetails: {
        moduleId: moduleObjectId,
        lessonId: lessonObjectId,
        pageIndex: currentPage,
      },
    });
  }, [lesson, module, lessonObjectId, moduleObjectId, currentPage]);

  useEffect(() => {
    if (lesson && (currentPage > lesson.pages.length || currentPage < 0)) {
      setCurrentPage(0);
    }
    const url = new URL(window.location.href);
    url.searchParams.set("page", currentPage.toString());
    window.history.replaceState({}, "", url.toString());
  }, [currentPage, lesson]);

  useEffect(() => {
    if (recaps && recaps.length > 0) {
      setRecapState(recaps[0].content);
      setAnswers(
        recaps[0].content.reduce((acc, pageAnswers) => {
          Object.entries(pageAnswers).forEach(([variable, answer]) => {
            if (answer && typeof answer === "object" && "value" in answer) {
              acc[variable] = { value: answer.value };
              if ("displayAs" in answer) {
                acc[variable].displayAs = answer.displayAs;
              }
            }
          });
          return acc;
        }, {}),
      );
      if (recaps[0].pages_visited) {
        const pageIds = new Set(recaps[0].pages_visited);
        setVisitedPageIds(pageIds);
      }
    } else if (lesson) {
      setRecapState(lesson.pages.map(() => ({})));
      setAnswers({});
    }
  }, [recaps, lesson]);

  const updateVisitedPages = async (pageId: string) => {
    if (!pageId || !recaps || recaps.length === 0) return;

    const newVisitedPageIds = new Set(visitedPageIds).add(pageId);
    setVisitedPageIds(newVisitedPageIds);

    try {
      await mutation.mutate({
        ...recaps[0],
        content: recapState,
        pages_visited: Array.from(newVisitedPageIds),
      });
    } catch (error) {
      setVisitedPageIds(visitedPageIds);
    }
  };

  const handleContentChange = (
    variable: string,
    label: string,
    value: string | number | string[],
    displayAs?: string,
  ) => {
    setAnswers((prevAnswers) => {
      const newAnswers = {
        ...prevAnswers,
        [variable]: { value, displayAs },
      };
      synchForEach(variable, newAnswers);
      synchPriorityList(variable, newAnswers);
      return newAnswers;
    });
    setRecapState((prevRecapState) => {
      const newRecap = [...prevRecapState];
      // Update the variable in all pages where it exists, or create it in current page if it doesn't exist
      return newRecap.map((pageAnswers, index) => ({
        ...pageAnswers,
        ...(variable in pageAnswers || index === currentPage
          ? {
              [variable]: {
                ...(variable in pageAnswers ? pageAnswers[variable] : {}),
                label,
                value,
                displayAs,
              },
            }
          : {}),
      }));
    });
  };

  const filterAnsweredQuestions = (content: RecapContent): RecapEntry[] => {
    return content
      ?.map((entry) =>
        Object.keys(entry).reduce((acc: RecapEntry, key) => {
          if (entry[key]?.value) {
            acc[key] = {
              label: parsePreviousAnswers(entry[key].label, answers),
              value: entry[key].value,
              displayAs: entry[key]?.displayAs,
            };
          }
          return acc;
        }, {}),
      )
      .filter((entry) => Object.keys(entry).length > 0);
  };

  const saveCurrentPageState = async () => {
    if (lesson?.pages[currentPage]?._id) {
      await updateVisitedPages(lesson.pages[currentPage]._id);
    }
  };

  const handlePageChange = async (direction: "next" | "back") => {
    await saveCurrentPageState();

    const nextPageIndex =
      direction === "next" ? currentPage + 1 : currentPage - 1;
    if (nextPageIndex < 0) {
      navigate(`/topics/${moduleSlugOrId}`);
      return;
    }

    window.scrollTo(0, 0);
    setCurrentPage(nextPageIndex);
  };

  const handleFeedbackChange = (newFeedback: FeedbackI | null) => {
    setFeedback(newFeedback);
  };

  const saveAnswersToJournal = async (includeIndividualTakeaways: boolean) => {
    if (!recapState || !lesson) return;

    const filteredRecapContent = filterAnsweredQuestions(recapState);

    await responseMut.mutateAsync({
      module: moduleObjectId,
      lesson: lessonObjectId,
      title: lesson.title,
      keyTakeaway: undefined,
      type: ResponseEnum.SECTION_RECAP,
      content: Object.assign({}, ...filteredRecapContent),
      isCompletion: true,
    });

    if (includeIndividualTakeaways) {
      const keyTakeaways = getKeyTakeaways();
      for (const takeaway of keyTakeaways) {
        if (Object.keys(takeaway.content).length > 0) {
          await responseMut.mutateAsync({
            module: moduleObjectId,
            lesson: lessonObjectId,
            title: takeaway.takeaway,
            keyTakeaway: takeaway.takeaway,
            type: ResponseEnum.SECTION_RECAP,
            content: takeaway.content,
          });
        }
      }
    }
  };

  const handleSaveAndContinue = async (shouldSaveToToolbox?: boolean) => {
    if (!recapState || !lesson) return;

    const filteredRecapContent = filterAnsweredQuestions(recapState);

    try {
      const saveAnswersToLog = userPreferences?.saveAnswersToLog ?? true;
      const shouldSaveIndividualTakeaways =
        saveAnswersToLog || shouldSaveToToolbox;
      await saveAnswersToJournal(shouldSaveIndividualTakeaways);

      const toolboxLink = module?._id
        ? `/my-toolboxes/${module._id}?tab=journal`
        : "/my-toolboxes?tab=journal";

      const currentUrl = window.location.pathname + window.location.search;

      const handleViewClick = async () => {
        clearNotifications();
        savePageInput();
        navigate(toolboxLink, { state: { from: currentUrl } });
      };

      if (filteredRecapContent.length === 0) {
        notify(
          "success",
          <>
            Nice work, you&apos;ve completed this section!{" "}
            <span
              className="underline font-bold hover:text-gray-700 cursor-pointer"
              onClick={handleViewClick}
            >
              View here.
            </span>
          </>,
        );
      } else {
        notify(
          "success",
          <>
            Nice work! Your responses were added to My Journal in My Toolbox.{" "}
            <span
              className="underline font-bold hover:text-gray-700 cursor-pointer"
              onClick={handleViewClick}
            >
              View here.
            </span>
          </>,
        );
      }

      if (recaps && recaps.length > 0) {
        await deleteMut.mutateAsync(recaps[0]._id);
      }
    } catch (error) {
      notify(
        "error",
        "There was an error saving your progress. Please try again.",
      );
    }
  };

  const savePageInput = () => {
    mutation.mutate({ ...recaps[0], content: recapState });
  };

  // Function to Call the function present inside the PriorityList component
  const synchPriorityList = (
    variable: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    answers: Record<string, any>,
  ) => {
    Object.entries(priorityListRefs.current).forEach(([, ref]) => {
      if (ref && typeof ref.syncVariableChange === "function") {
        ref.syncVariableChange(variable, answers);
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRestart = async (type: "keep" | "save" | "clear") => {
    if (!recaps || recaps.length === 0 || !lesson) return;

    try {
      switch (type) {
        case "keep":
          // Keep current answers, just go to first page
          await savePageInput();
          setCurrentPage(0);
          window.scrollTo(0, 0);
          break;

        case "save": {
          const filteredRecapContent = filterAnsweredQuestions(recapState);
          if (filteredRecapContent.length > 0) {
            await saveAnswersToJournal(true);
            const toolboxLink = module?._id
              ? `/my-toolboxes/${module._id}?tab=journal`
              : "/my-toolboxes?tab=journal";
            const currentUrl =
              window.location.pathname + window.location.search;
            const handleViewClick = async () => {
              clearNotifications();
              savePageInput();
              navigate(toolboxLink, { state: { from: currentUrl } });
            };
            notify(
              "success",
              <>
                Nice work! Your responses were added to My Journal in My
                Toolbox.{" "}
                <span
                  className="underline font-bold hover:text-gray-700 cursor-pointer"
                  onClick={handleViewClick}
                >
                  View here.
                </span>
              </>,
            );
          }
          // Delete current recap and create a new one
          await deleteMut.mutateAsync(recaps[0]._id);
          await createOrGetRecapMut.mutateAsync({
            lesson: lessonObjectId!,
            module: moduleObjectId!,
          });
          setCurrentPage(0);
          window.scrollTo(0, 0);
          break;
        }

        case "clear":
          // Delete current recap and create a new one
          await deleteMut.mutateAsync(recaps[0]._id);
          await createOrGetRecapMut.mutateAsync({
            lesson: lessonObjectId!,
            module: moduleObjectId!,
          });
          setCurrentPage(0);
          window.scrollTo(0, 0);
          break;
      }
    } catch (error) {
      notify(
        "error",
        "There was an error processing your request. Please try again.",
      );
    }
  };

  const getTakeawayContent = (takeaway: string) => {
    const pageIndexesWithTakeaway =
      lesson?.pages
        .map((page, index) => (page.keyTakeaways === takeaway ? index : -1))
        .filter((index) => index !== -1) || [];

    return pageIndexesWithTakeaway.reduce((acc, pageIndex) => {
      const pageRecaps = recapState[pageIndex] || {};
      const filteredRest = Object.fromEntries(
        Object.entries(pageRecaps)
          .filter(([, val]) => val && typeof val === "object" && "value" in val)
          .map(([key, val]) => {
            const recapValue = val as RecapValue;
            return [
              key,
              {
                ...recapValue,
                label: parsePreviousAnswers(recapValue.label, answers),
              },
            ];
          }),
      );
      return { ...acc, ...filteredRest };
    }, {});
  };

  const getKeyTakeaways = () => {
    const uniqueTakeaways = new Set(
      lesson.pages
        .map((page) => page.keyTakeaways)
        .filter((takeaway) => takeaway && takeaway.trim() !== ""),
    );
    return Array.from(uniqueTakeaways).map((takeaway) => {
      const content = getTakeawayContent(takeaway);
      return {
        takeaway,
        content,
      };
    });
  };

  const renderBottomNavigationBar = () => {
    const bottomNavigationBarType =
      lesson.bottomNavigationBarType || BottomNavigationBarType.MULTI_PAGE;

    switch (bottomNavigationBarType) {
      case BottomNavigationBarType.MULTI_PAGE:
        return (
          <FadingNavigation bottomOffset={0} className="bg-white shadow-md">
            <LessonBottomNavigation
              currentPage={currentPage}
              currentPageData={lesson.pages[currentPage]}
              module={module!}
              lesson={lesson!}
              onPageChange={handlePageChange}
              onToolboxClick={async () => {
                await saveCurrentPageState();
                savePageInput();
                navigate(`/my-toolboxes/${moduleObjectId}`, {
                  state: { from: learnUrl },
                });
              }}
              onBeforeNavigateToToolbox={async () => {
                await saveCurrentPageState();
                savePageInput();
              }}
              hasToolboxContent={hasToolboxContent}
              answers={answers}
            />
          </FadingNavigation>
        );
      case BottomNavigationBarType.SINGLE_PAGE:
        return (
          <div className="space-y-2 px-2">
            <Button
              className="w-full h-fit"
              variant="secondary"
              roundedFull
              onClick={() => navigate(`/topics/${moduleSlugOrId}`)}
              size="md"
            >
              Return to Menu for {module?.title}
            </Button>
            <Button
              className="w-full h-fit"
              variant="secondary"
              roundedFull
              onClick={() => navigate("/explore")}
              size="md"
            >
              Return to Explore Page
            </Button>
          </div>
        );
      case BottomNavigationBarType.NONE:
        return null;
      default: {
        const _exhaustiveCheck: never = bottomNavigationBarType;
        throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
      }
    }
  };

  const handleSideNavLessonSelect = (
    selectedLesson: LessonI,
    pageIndex: number = 0,
  ) => {
    const moduleSlug = module?.slug ?? moduleId;
    const lessonSlug = selectedLesson.slug ?? selectedLesson._id;
    navigate(`/topics/${moduleSlug}/sections/${lessonSlug}`, {
      state: { initialPage: pageIndex },
    });
  };

  const handleNavigateToModulePage = async () => {
    await saveCurrentPageState();
    savePageInput();
    navigate(`/topics/${module?.slug ?? moduleId}`);
  };

  if (isLoading || isLoadingRecap || !recapState)
    return <Spinner className="mx-auto" />;
  if (isError || isErrorRecap || !lesson || !recaps)
    return (
      <ErrorDisplay error={lessonError || recapsError} messageSource="api" />
    );

  if (!lesson || !recaps || !module) {
    return <LoadingCard className="mt-4" />;
  }

  const lessonWithTopicTocShellClass = topicTocEnabled
    ? "flex flex-col lg:flex-row gap-8 max-w-6xl mx-auto px-2 sm:px-4 lg:px-8 pt-4 lg:py-8 pb-40 lg:pb-8 w-full"
    : "flex flex-col flex-grow mx-auto w-full max-w-3xl px-2 sm:px-4 text-left pb-24";

  const lessonWithTopicTocMainColumnClass = topicTocEnabled
    ? "flex flex-col flex-grow min-w-0 text-left pb-24"
    : "contents";

  return (
    <div className="flex flex-col flex-grow w-full">
      {currentPage >= lesson.pages.length ? (
        <>
          {/* Top Navigation for Congratulations page */}
          <div
            className={`sticky top-0 left-0 right-0 z-10${
              topicTocEnabled ? " lg:hidden" : ""
            }`}
          >
            <LessonTopNavigation
              module={module!}
              lesson={lesson!}
              currentPage={currentPage}
              totalPages={lesson.pages.length + 1}
              progressRef={keyTakeawaysProgressRef}
              onExploreClick={async () => {
                await saveCurrentPageState();
                savePageInput();
                navigate("/explore");
              }}
              onLearnClick={handleNavigateToModulePage}
            />
          </div>
          <div className="hidden">
            <Progress
              ref={keyTakeawaysProgressRef}
              currentStep={lesson.pages.length}
              steps={[...lesson.pages.map((page) => page.title), "Recap"]}
              onClick={setCurrentPage}
              centered={true}
              moduleId={module._id}
              moduleTitle={module.title}
              moduleIcon={module.icon}
              lessonTitle={lesson.title}
              onRestart={handleRestart}
              hasAnswers={hasAnswersToSave(recapState)}
              showTutorialTip={false}
            />
          </div>
          <div className={lessonWithTopicTocShellClass}>
            {topicTocEnabled && (
              <LessonSideNav
                module={module!}
                onModuleNavigate={handleNavigateToModulePage}
                currentLesson={lesson!}
                currentPage={currentPage}
                onPageSelect={async (pageIndex) => {
                  await saveCurrentPageState();
                  setCurrentPage(pageIndex);
                  window.scrollTo(0, 0);
                }}
                onLessonSelect={handleSideNavLessonSelect}
                recapPageIndex={lesson!.pages.length}
              />
            )}
            <div className={lessonWithTopicTocMainColumnClass}>
              <div className="w-full max-w-2xl mx-auto">
                <h1 className="text-2xl font-bold my-3 text-center">
                  Congratulations!
                </h1>
                <KeyTakeaways
                  keyTakeaways={getKeyTakeaways()}
                  lessonTitle={lesson.title}
                />
              </div>
              <Feedback
                lesson={lesson}
                moduleId={moduleObjectId}
                onFeedbackChange={handleFeedbackChange}
              />
              {/* Completion buttons moved to page content */}
              <div className="px-0 sm:px-4 mt-4">
                <div className="flex flex-col space-y-2">
                  <LessonCompletionButton
                    moduleId={module!._id}
                    lessonId={lessonObjectId!}
                    handleSaveAndContinue={handleSaveAndContinue}
                    saveAnswersToLog={userPreferences?.saveAnswersToLog}
                    askEveryTime={userPreferences?.askEveryTime}
                    recapId={recaps?.[0]?._id}
                    lessonTitle={lesson.title}
                    hasAnswers={hasAnswersToSave(recapState)}
                    destination="next-lesson"
                    buttonVariant="primary"
                    feedback={feedback}
                    feedbackMutation={feedbackMut}
                  />
                  <LessonCompletionButton
                    moduleId={module!._id}
                    lessonId={lessonObjectId!}
                    handleSaveAndContinue={handleSaveAndContinue}
                    saveAnswersToLog={userPreferences?.saveAnswersToLog}
                    askEveryTime={userPreferences?.askEveryTime}
                    recapId={recaps?.[0]?._id}
                    lessonTitle={lesson.title}
                    hasAnswers={hasAnswersToSave(recapState)}
                    destination="module-menu"
                    buttonVariant="secondary"
                    feedback={feedback}
                    feedbackMutation={feedbackMut}
                  />
                </div>
              </div>
              <div className="h-48"></div>
            </div>
          </div>
          {/* Bottom Navigation for Congratulations page */}
          <FadingNavigation bottomOffset={0} className="bg-white shadow-md">
            <div className="w-full bg-white border-t border-gray-200 shadow-md">
              <div className="w-full max-w-none px-4 pt-4 pb-8 mb-6">
                <div className="flex items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto">
                  {/* Back Button */}
                  <Button.Circle
                    variant="primary"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="h-10 w-10 flex items-center justify-center flex-shrink-0"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </Button.Circle>

                  {/* Go To Toolbox Button - only show if toolbox has content */}
                  {hasToolboxContent && (
                    <GoToToolboxButton
                      onClick={() => {
                        savePageInput();
                        navigate(`/my-toolboxes/${moduleObjectId}`, {
                          state: { from: learnUrl },
                        });
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </FadingNavigation>
        </>
      ) : (
        <>
          <div className={topicTocEnabled ? "lg:hidden" : undefined}>
            <LessonTopNavigation
              module={module!}
              lesson={lesson!}
              currentPage={currentPage}
              totalPages={lesson.pages.length + 1}
              progressRef={progressRef}
              onExploreClick={async () => {
                await saveCurrentPageState();
                savePageInput();
                navigate("/explore");
              }}
              onLearnClick={handleNavigateToModulePage}
            />
          </div>
          <div className="hidden">
            <Progress
              ref={progressRef}
              currentStep={currentPage}
              steps={[...lesson.pages.map((page) => page.title), "Recap"]}
              onClick={setCurrentPage}
              centered={true}
              moduleId={module._id}
              moduleTitle={module.title}
              moduleIcon={module.icon}
              lessonTitle={lesson.title}
              onRestart={handleRestart}
              hasAnswers={hasAnswersToSave(recapState)}
              showTutorialTip={false}
            />
          </div>
          <div className={lessonWithTopicTocShellClass}>
            {topicTocEnabled && (
              <LessonSideNav
                module={module!}
                onModuleNavigate={handleNavigateToModulePage}
                currentLesson={lesson!}
                currentPage={currentPage}
                onPageSelect={async (pageIndex) => {
                  await saveCurrentPageState();
                  setCurrentPage(pageIndex);
                  window.scrollTo(0, 0);
                }}
                onLessonSelect={handleSideNavLessonSelect}
                recapPageIndex={lesson!.pages.length}
              />
            )}
            <div className={lessonWithTopicTocMainColumnClass}>
              <div className="w-full max-w-2xl mx-auto mt-6 mb-48 space-y-8 px-4">
                <div className="flex items-center justify-between gap-2">
                  {lesson.pages[currentPage]?.title && (
                    <h2 className="text-xl font-semibold text-gray-900">
                      {lesson.pages[currentPage].title}
                    </h2>
                  )}
                  {canEditContent && (
                    <Button.Circle
                      variant="secondary"
                      onClick={() =>
                        navigate(`/admin/edit-lesson/${lessonObjectId}`, {
                          state: {
                            moduleId: moduleObjectId,
                            pageIndex: currentPage,
                          },
                        })
                      }
                      className="h-8 w-8 flex-shrink-0"
                      title="Edit this page in content manager"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button.Circle>
                  )}
                </div>
                {lesson.pages.length > 0 &&
                  renderPageComponents(
                    lesson.pages[currentPage].components,
                    currentPage,
                    recapState[currentPage],
                    answers,
                    handleContentChange,
                    {
                      [ComponentEnum.LESSON_LINK]: {
                        savePageInput,
                        lesson: lesson,
                      },
                      [ComponentEnum.FOR_EACH]: {
                        onRefAssign: (
                          componentId: string,
                          ref: ForEachComponentRef | null,
                        ) => {
                          forEachRefs.current[componentId] = ref;
                        },
                      },
                      [ComponentEnum.PRIORITY_LIST]: {
                        onRefAssign: (
                          componentId: string,
                          ref: PriorityListRef | null,
                        ) => {
                          priorityListRefs.current[componentId] = ref;
                        },
                      },
                      [ComponentEnum.VIDEO]: {
                        lesson,
                        moduleId: moduleObjectId,
                        pageIndex: currentPage,
                        pageId: lesson.pages[currentPage]?._id,
                      },
                      [ComponentEnum.ADD_TO_CALENDAR]: {
                        lessonPageUrl: `${window.location.origin}${learnUrl}`,
                        toolboxUrl: moduleObjectId
                          ? `${window.location.origin}/my-toolboxes/${moduleObjectId}`
                          : undefined,
                        isPageInToolbox,
                        onAddToToolbox: async () => {
                          const page = lesson.pages[currentPage];
                          if (!page?._id) return;
                          const payload = buildPageBookmarkPayload(
                            page,
                            lesson,
                            module ?? undefined,
                            answers,
                          );
                          await createPageBookmarkMut.mutateAsync(payload);
                        },
                      },
                      ALL: {
                        lesson,
                        savePageInput,
                      },
                    },
                    "textarea",
                  )}
              </div>
              {renderBottomNavigationBar()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LessonPage;
